/* DealRoomMobile.jsx — Apple × Linear × Stripe operational command
 * centre for ONE live property transaction, on mobile.
 *
 * NOT a shrunk desktop layout. Built mobile-first against the
 * approved design comp. Renders only on viewports < md.
 *
 * Architectural rules:
 *   • Pure presentation — every data point flows in from
 *     DealRoomV3Page (the single source of truth).
 *   • Uses the existing sheets (Email, Call, Upload, Task, Message
 *     All, AddNote) via the parent's `onOpenSheet` switchboard, so
 *     reply-capture / chase / call-log already work.
 *   • Inline composer posts notes via /api/v2/deals/{id}/activity
 *     (the same endpoint AddNoteSheet uses) so we never need a
 *     modal for a one-line update.
 *
 * Strict spacing (per spec):
 *   • 16px outer padding             → px-4
 *   • 12px card padding              → p-3 / p-3.5
 *   • 18-22px section gaps           → mt-4 / mt-5
 *   • 44-48px min touch target       → h-[46px]
 *   • soft atmospheric shadows       → 0 6px 18px -14px rgba(15,23,42,.08)
 *   • minimal borders                → slate-100 / slate-200
 */
import React, { useCallback, useMemo, useState } from "react";
import { calendarDaysUntil } from "../../lib/waitDuration";
import { SaleAgreedElapsed, fmtSaleAgreedDate } from "./SaleAgreedElapsed";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Star, MoreHorizontal, Send, Pencil, Phone, MessageCircle,
  FileText, MessageSquare, Bell, ChevronDown, ChevronRight,
  Calendar, Flag, Paperclip, Mic, Mail, Upload, CheckCircle2,
  Users, Folder, Clock, Sparkles, Check, Circle, FilePlus2,
} from "lucide-react";
import { api } from "../../lib/api";
import { saveDealReturnState, peekDealReturnState } from "../../lib/dealReturnState";
import { invalidateDealActivity } from "../../hooks/useDealActivity";
import { sendOperationalEmail } from "../../lib/emailClient";
import { bumpTodaySignal } from "../../hooks/useTodaySignal";
import { createContactForParticipant } from "../../lib/participantContactCreate";
import { resolveDealJourneyProgress } from "../../data/transactionJourney";

/* ─── Helpers ───────────────────────────────────────────────────── */
const _MS_DAY = 86_400_000;

// Exchanged / completed deals are never "overdue" against their exchange target.
const _EXCHANGED_STAGES = new Set(["exchanged", "completion", "completed"]);
const isDealExchanged = (deal) =>
  !!(deal?.exchangedAt || deal?.exchanged_at) || _EXCHANGED_STAGES.has(String(deal?.stage || "").toLowerCase());

// Truthful wording for the canonical exchange target; null when there is no date.
export function exchangeTargetStatus(dTo, exchanged) {
  if (exchanged) return { short: "Exchanged", ring: "✓", overdue: false };
  if (dTo === null || dTo === undefined) return null;
  if (dTo < 0) {
    const n = Math.abs(dTo);
    return { short: `${n} day${n === 1 ? "" : "s"} overdue`, ring: `${n}d`, overdue: true };
  }
  if (dTo === 0) return { short: "Due today", ring: "0d", overdue: false };
  return { short: `${dTo} day${dTo === 1 ? "" : "s"} remaining`, ring: `${dTo}d`, overdue: false };
}

// Blockers API uses "solicitor_seller"; participants use "seller_solicitor".
const normaliseRole = (r) => {
  const v = String(r || "").toLowerCase().trim().replace(/\s+/g, "_");
  if (v === "solicitor_seller") return "seller_solicitor";
  if (v === "solicitor_buyer") return "buyer_solicitor";
  return v;
};
const daysBetween = (iso) => calendarDaysUntil(iso);
const formatDateLong = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};
const formatRel = (iso) => {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const mins = Math.floor((Date.now() - t) / 60_000);
  if (mins < 1)  return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 6) return `${hrs}h ago`;
  const d = new Date(t);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    const h = d.getHours(); const m = String(d.getMinutes()).padStart(2, "0");
    const ap = h >= 12 ? "pm" : "am"; const h12 = ((h + 11) % 12) + 1;
    return `Today · ${h12}:${m}${ap}`;
  }
  const y = new Date(today); y.setDate(today.getDate() - 1);
  if (d.toDateString() === y.toDateString()) {
    const h = d.getHours(); const m = String(d.getMinutes()).padStart(2, "0");
    const ap = h >= 12 ? "pm" : "am"; const h12 = ((h + 11) % 12) + 1;
    return `Yesterday · ${h12}:${m}${ap}`;
  }
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};
const initials = (name) => (name || "?")
  .trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase();

/* ─── Future-day detection ──────────────────────────────────────────
 * Quiet, high-confidence parser. Returns the nearest future day the
 * agent referred to in their note, or null. ONLY triggers on explicit
 * weekday names, "tomorrow", or "next {weekday}" — never on vague
 * phrases like "next week" or "soon". Designed to feel like
 * remembering, not guessing.
 */
const _WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
function detectFollowUpDay(text) {
  if (!text) return null;
  const s = text.toLowerCase();
  const now = new Date();
  const todayIdx = now.getDay();

  // 1) "tomorrow"
  if (/\btomorrow\b/.test(s)) {
    const d = new Date(now); d.setDate(now.getDate() + 1);
    return _packDay(d, "Tomorrow");
  }

  // 2) "next {weekday}" — always 7-13 days ahead
  let m = s.match(/\bnext\s+(sun|mon|tue|wed|thu|fri|sat)(?:day|nesday|rsday|urday)?\b/);
  if (m) {
    const target = _WEEKDAYS.findIndex((w) => w.startsWith(m[1]));
    if (target >= 0) {
      let delta = (target - todayIdx + 7) % 7;
      delta = delta === 0 ? 7 : delta + 7; // strictly *next* week
      const d = new Date(now); d.setDate(now.getDate() + delta);
      return _packDay(d, `Next ${_titleCase(_WEEKDAYS[target])}`);
    }
  }

  // 3) Bare weekday — must be preceded by an intent verb ("by",
  //    "until", "on", "respond", "update", "confirm", "expected",
  //    "back", "wait", "wait until"…) to avoid false positives on
  //    historical references.
  m = s.match(/\b(?:by|until|on|expect(?:ed|ing)?|due|wait(?:ing)?|back|respond(?:ing|s)?|update|updating|confirm(?:ing|s)?|call(?:ing|s)?|reply(?:ing)?|send(?:ing)?|return(?:ing|s)?)\s+(?:by\s+|until\s+|on\s+|this\s+)?(sun|mon|tue|wed|thu|fri|sat)(?:day|nesday|rsday|urday)?\b/);
  if (m) {
    const target = _WEEKDAYS.findIndex((w) => w.startsWith(m[1]));
    if (target >= 0) {
      let delta = (target - todayIdx + 7) % 7;
      if (delta === 0) delta = 7; // never today — assume next occurrence
      const d = new Date(now); d.setDate(now.getDate() + delta);
      return _packDay(d, _titleCase(_WEEKDAYS[target]));
    }
  }
  return null;
}
function _titleCase(s) { return s ? s[0].toUpperCase() + s.slice(1) : s; }
function _packDay(d, label) {
  // 9:00 AM local time — calm, operational default for follow-ups.
  d.setHours(9, 0, 0, 0);
  return { day: label, iso: d.toISOString(), date: d };
}

/* Risk tone — Healthy = indigo · Medium = amber · Critical = rose */
const RISK_TONES = {
  healthy:  { dot: "#6366F1", chipBg: "#EEF0FE", chipFg: "#4338CA", label: "Healthy" },
  medium:   { dot: "#F59E0B", chipBg: "#FEF3E8", chipFg: "#B45309", label: "Medium Risk" },
  critical: { dot: "#F43F5E", chipBg: "#FFEEF1", chipFg: "#BE123C", label: "Critical Risk" },
};
// Chip label: saved At Risk status first, otherwise the real reason behind the tone.
const riskFor = (vm, deal) => {
  const blockers = vm?.blockerCount || 0;
  const dTo = vm?.dToExchange;
  const nB = `${blockers} blocker${blockers === 1 ? "" : "s"}`;
  if (deal?.status === "At Risk") return { ...RISK_TONES.critical, label: "At Risk" };
  if (blockers >= 3) return { ...RISK_TONES.critical, label: nB };
  if (dTo !== null && dTo !== undefined && dTo < 0 && !isDealExchanged(deal)) return { ...RISK_TONES.critical, label: "Exchange overdue" };
  if (blockers >= 1) return { ...RISK_TONES.medium, label: nB };
  if (vm?.slowing) return { ...RISK_TONES.medium, label: "Slowing" };
  return RISK_TONES.healthy;
};

/* Activity row taxonomy — calm operational language */
const FEED_ICON = {
  email_inbound:     { Icon: Mail,       bg: "rgba(99,102,241,0.10)",  fg: "#4338CA" },
  email_outbound:    { Icon: Send,       bg: "rgba(99,102,241,0.10)",  fg: "#4338CA" },
  document:          { Icon: FileText,   bg: "rgba(99,102,241,0.10)",  fg: "#4338CA" },
  milestone:         { Icon: CheckCircle2, bg: "rgba(16,185,129,0.10)", fg: "#047857" },
  chase:             { Icon: Flag,       bg: "rgba(99,102,241,0.10)",  fg: "#4338CA" },
  call:              { Icon: Phone,      bg: "rgba(245,158,11,0.12)",  fg: "#B45309" },
  note:              { Icon: MessageSquare, bg: "rgba(99,102,241,0.10)", fg: "#4338CA" },
  task:              { Icon: CheckCircle2, bg: "rgba(99,102,241,0.10)", fg: "#4338CA" },
  scheduled:         { Icon: Calendar,   bg: "rgba(99,102,241,0.10)",  fg: "#4338CA" },
  risk:              { Icon: Flag,       bg: "rgba(244,63,94,0.10)",   fg: "#BE123C" },
  default:           { Icon: Sparkles,   bg: "rgba(148,163,184,0.12)", fg: "#475569" },
};

/* Map raw activity into a calm feed entry with human title +
 * subtitle. The goal is wording that sounds like real
 * transaction coordination, never database events.
 *
 *   "Document uploaded"  ─►  "TA6 received from seller solicitor"
 *   "Call logged"        ─►  "Spoke with buyer solicitor"
 *   "Message"            ─►  "Chased Rosanna Blake" / "Sent
 *                            update to seller side"
 */
const _ROLE_TO_SIDE = {
  buyer:            "buyer",
  buyer_solicitor:  "buyer solicitor",
  seller:           "seller",
  seller_solicitor: "seller solicitor",
  mortgage_broker:  "mortgage broker",
  agent:            "agent",
};
function _roleLabel(role) {
  return _ROLE_TO_SIDE[(role || "").toLowerCase()] || "participant";
}
function _firstName(name) {
  return String(name || "").trim().split(/\s+/)[0] || "";
}

function decorateActivity(row) {
  const at = (row.activity_type || "").toLowerCase();
  const meta = row.meta || {};

  // ── Inbound reply ──────────────────────────────────────────
  if (at === "message" && meta.direction === "inbound") {
    const who = _firstName(meta.participant_name || meta.from_name || meta.from_email) || "Participant";
    const body = (meta.body_preview || meta.body_text || "").trim();
    return {
      kind: "email_inbound",
      title: `${who} replied`,
      subtitle: body ? `“${body.slice(0, 120)}${body.length > 120 ? "…" : ""}”` : meta.subject,
    };
  }

  // ── Outbound email (chase / update / generic) ──────────────
  if (at === "message" && meta.email && meta.direction === "outbound") {
    const kind = meta.kind || "email";
    // Pick the most specific recipient if there's exactly one.
    const recipients = Array.isArray(meta.recipients) ? meta.recipients : [];
    const single = recipients.length === 1 ? recipients[0] : null;
    const recipientName = single ? _firstName(single.full_name || single.name) : "";
    const roleSide = single ? _roleLabel(single.role || single.participant_type) : "";

    if (kind === "chase") {
      const title = recipientName
        ? `Chased ${recipientName}`
        : roleSide
          ? `Chased ${roleSide}`
          : "Chase sent";
      return { kind: "chase", title, subtitle: meta.subject || "Follow up on outstanding enquiries" };
    }
    if (kind === "seller_update") {
      return { kind: "email_outbound", title: "Sent weekly update to seller", subtitle: meta.subject || "" };
    }
    if (kind === "broadcast") {
      return { kind: "email_outbound", title: "Sent update to all parties", subtitle: meta.subject || "" };
    }
    if (kind === "request") {
      return { kind: "document", title: recipientName ? `Requested document from ${recipientName}` : "Document request sent", subtitle: meta.subject || "" };
    }
    if (kind === "reply") {
      return { kind: "email_outbound", title: recipientName ? `Replied to ${recipientName}` : "Reply sent", subtitle: meta.subject || "" };
    }
    const title = recipientName
      ? `Emailed ${recipientName}`
      : roleSide
        ? `Emailed ${roleSide}`
        : "Email sent to participants";
    return { kind: "email_outbound", title, subtitle: meta.subject || "" };
  }

  // ── Note (plain timeline post) ─────────────────────────────
  if (at === "message" && !meta.email) {
    return { kind: "note", title: row.title || "Note added", subtitle: row.description || "" };
  }

  // ── Documents (received from someone, with side language) ──
  // Detect titles that are already past-tense / descriptive so we
  // don't say "Requested Searches commissioned" — agents wrote
  // those titles meaning what they say.
  const _ALREADY_DESCRIPTIVE = /\b(received|uploaded|ordered|commissioned|issued|requested|signed|completed|returned|booked)\b/i;
  if (at === "document_upload") {
    const docName = row.title || meta.name || meta.file_name || "Document";
    const fromName = meta.uploaded_by_name || meta.participant_name || meta.from_name || "";
    const fromRole = _roleLabel(meta.participant_role || meta.uploaded_by_role);
    if (_ALREADY_DESCRIPTIVE.test(docName)) {
      return { kind: "document", title: docName, subtitle: row.description || "" };
    }
    const title = fromName
      ? `${docName} received from ${_firstName(fromName)}`
      : fromRole !== "participant"
        ? `${docName} received from ${fromRole}`
        : `${docName} received`;
    return { kind: "document", title, subtitle: row.description || "" };
  }
  if (at === "document_request") {
    const docName = row.title || meta.name || "Document";
    if (_ALREADY_DESCRIPTIVE.test(docName)) {
      return { kind: "document", title: docName, subtitle: row.description || "" };
    }
    const fromRole = _roleLabel(meta.participant_role);
    const title = fromRole !== "participant"
      ? `Requested ${docName} from ${fromRole}`
      : `Requested ${docName}`;
    return { kind: "document", title, subtitle: row.description || "" };
  }

  // ── Milestones / stage changes (kept human via title) ──────
  if (at === "milestone_completed" || at === "stage_change") {
    return { kind: "milestone", title: row.title || "Milestone completed", subtitle: row.description || "" };
  }

  // ── Calls — "Spoke with buyer solicitor" pattern ───────────
  if (at === "call_logged" || at === "call") {
    const partyRole = _roleLabel(meta.participant_role || meta.with_role);
    const partyName = _firstName(meta.participant_name || meta.with_name);
    const outcome = (meta.outcome || "").toLowerCase();
    let title;
    if (outcome === "spoke" || outcome === "answered") {
      title = partyName ? `Spoke with ${partyName}` : `Spoke with ${partyRole}`;
    } else if (outcome === "left_voicemail" || outcome === "voicemail") {
      title = partyName ? `Left voicemail for ${partyName}` : `Left voicemail for ${partyRole}`;
    } else if (outcome === "no_answer") {
      title = partyName ? `No answer from ${partyName}` : `No answer from ${partyRole}`;
    } else if (outcome === "callback") {
      title = partyName ? `${partyName} will call back` : `${partyRole} will call back`;
    } else {
      title = row.title || (partyName ? `Spoke with ${partyName}` : `Spoke with ${partyRole}`);
    }
    return { kind: "call", title, subtitle: row.description || "" };
  }

  // ── Chases / reminders / risk ──────────────────────────────
  if (at === "reminder_sent") {
    return { kind: "chase", title: row.title || "Chase sent", subtitle: row.description || "" };
  }
  if (at === "risk_flagged") {
    return { kind: "risk", title: row.title || "Blocker raised", subtitle: row.description || "" };
  }
  if (at === "risk_cleared") {
    return { kind: "milestone", title: row.title || "Blocker cleared", subtitle: row.description || "" };
  }

  // ── Tasks ──────────────────────────────────────────────────
  if (at === "task_created") {
    // Scheduled follow-up — calm, "ChainFlow remembered for me".
    if (meta.kind === "scheduled_chase") {
      const day = meta.day_label || "later";
      const who = _firstName(meta.responsible_name) || "the responsible party";
      return {
        kind: "scheduled",
        title: `Follow-up scheduled for ${day}`,
        subtitle: `With ${who}${meta.blocker_context ? ` · ${meta.blocker_context}` : ""}`,
      };
    }
    return { kind: "task", title: row.title || "Task created", subtitle: row.description || "" };
  }
  if (at === "task_completed") {
    return { kind: "milestone", title: row.title || "Task completed", subtitle: row.description || "" };
  }

  return { kind: "default", title: row.title || at.replace(/_/g, " "), subtitle: row.description || "" };
}

/* ─── Top bar ────────────────────────────────────────────────────── */
function TopBar({ followed, onFollow }) {
  const nav = useNavigate();
  return (
    <div className="px-4 py-3 flex items-center justify-between" data-testid="dealroom-mobile-topbar">
      <button
        type="button"
        onClick={() => nav("/deals")}
        className="w-10 h-10 -ml-2 inline-flex items-center justify-center rounded-full active:bg-slate-100"
        aria-label="Back"
        data-testid="dealroom-mobile-back"
      >
        <ArrowLeft size={18} className="text-slate-700" strokeWidth={2} />
      </button>
      <div className="flex items-center gap-2">
        <span className="w-7 h-7 rounded-full inline-flex items-center justify-center"
          style={{ background: "rgba(99,102,241,0.10)" }}>
          <FileText size={13} className="text-violet-600" />
        </span>
        <h1 className="text-[15px] font-bold text-slate-900 tracking-tight">Deal Room</h1>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onFollow}
          className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-slate-200 active:bg-slate-100"
          aria-pressed={followed}
          data-testid="dealroom-mobile-follow"
        >
          <Star
            size={14}
            className={followed ? "text-violet-600 fill-violet-600" : "text-slate-500"}
            strokeWidth={2}
          />
        </button>
        <button
          type="button"
          className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-slate-200 active:bg-slate-100"
          aria-label="More"
          data-testid="dealroom-mobile-more"
        >
          <MoreHorizontal size={16} className="text-slate-600" />
        </button>
      </div>
    </div>
  );
}

/* ─── Hero ───────────────────────────────────────────────────────── */
function Hero({ deal, vm, prop, participants, activity, riskTone }) {
  const dTo = vm.dToExchange;
  const exStatus = exchangeTargetStatus(dTo, isDealExchanged(deal));
  const pct = dTo === null ? 0 : Math.max(0, Math.min(100, Math.round((1 - Math.min(dTo, 30) / 30) * 100)));
  // Last meaningful movement
  const lastAt = (activity && activity[0] && activity[0].created_at) || null;
  const visibleAvatars = (participants || []).slice(0, 3);
  const remainder = Math.max(0, (participants || []).length - visibleAvatars.length);

  return (
    <section
      className="mx-4 rounded-2xl bg-white px-4 py-4 relative overflow-hidden"
      style={{
        border: "1px solid #EFF0F5",
        boxShadow: "0 6px 18px -14px rgba(15,23,42,0.10)",
      }}
      data-testid="dealroom-mobile-hero"
    >
      {/* Atmospheric tint */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(140% 80% at 100% 0%, rgba(99,102,241,0.04), transparent 60%)",
      }} />
      <div className="relative flex items-start gap-4">
        {/* Left: address + chips */}
        <div className="flex-1 min-w-0">
          <h2 className="text-[20px] leading-[1.15] font-bold text-slate-900 tracking-tight">
            {vm.propertyAddress}
          </h2>
          <p className="text-[12px] text-slate-500 mt-0.5 truncate">
            {vm.subtitle || prop?.postcode || ""}
          </p>
          {(deal.saleAgreedDate || deal.sale_agreed_date) && (
            <p className="mt-1.5 flex items-center gap-1.5 flex-wrap text-[12px]" data-testid="dealroom-mobile-sale-agreed">
              <span className="text-slate-500">Sale agreed</span>
              <span className="font-semibold text-slate-900" data-testid="dealroom-mobile-sale-agreed-date">
                {fmtSaleAgreedDate(deal.saleAgreedDate || deal.sale_agreed_date)}
              </span>
              <SaleAgreedElapsed iso={deal.saleAgreedDate || deal.sale_agreed_date} testid="dealroom-mobile-sale-agreed-elapsed" />
            </p>
          )}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center text-[10.5px] font-bold px-2 h-[22px] rounded-md"
              style={{ background: "#EEF0FE", color: "#4338CA" }}
              data-testid="dealroom-mobile-stage"
            >
              {(() => {
                try {
                  return resolveDealJourneyProgress({ ...deal, completedSteps: deal.completedStepsApi || deal.completedSteps }).currentStageLabel;
                } catch { return null; }
              })() || String(deal.stage || "Active").replace(/_/g, " ")}
            </span>
            <span
              className="inline-flex items-center gap-1.5 text-[10.5px] font-bold px-2 h-[22px] rounded-md"
              style={{ background: riskTone.chipBg, color: riskTone.chipFg }}
              data-testid="dealroom-mobile-risk"
            >
              <span className="w-1 h-1 rounded-full" style={{ background: riskTone.dot }} />
              {riskTone.label}
            </span>
          </div>
          <p className="mt-3 text-[11.5px] text-slate-500 inline-flex items-center gap-1.5">
            <span aria-hidden className="w-1.5 h-1.5 rounded-full" style={{ background: "#10B981", boxShadow: "0 0 6px rgba(16,185,129,0.55)" }} />
            <span>Last movement</span>
            <span className="text-slate-300">·</span>
            <span className="tabular-nums text-slate-600 font-semibold">{lastAt ? formatRel(lastAt) : "Awaiting first update"}</span>
          </p>
        </div>

        {/* Right: exchange target + ring + avatars */}
        <div className="flex flex-col items-end gap-2 shrink-0" data-testid="dealroom-mobile-exchange">
          <p className="text-[10.5px] text-slate-500">Exchange target</p>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-[14.5px] font-bold text-slate-900 tabular-nums leading-tight">
                {formatDateLong(deal.targetExchange)}
              </p>
              <p
                className={`text-[10.5px] font-semibold mt-0.5 ${exStatus?.overdue ? "text-rose-600" : "text-violet-700"}`}
                data-testid="dealroom-mobile-exchange-status"
              >
                {exStatus ? exStatus.short : "—"}
              </p>
            </div>
            <CountdownRing pct={pct} label={exStatus ? exStatus.ring : "—"} tone={riskTone.dot} />
          </div>
          <div className="flex items-center -space-x-1.5 mt-1.5">
            {visibleAvatars.map((p, i) => (
              <span
                key={p.id || p.user_id || i}
                className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[10px] font-bold border-2 border-white"
                style={{ background: ["#EEF0FE", "#FEF3E8", "#FFEEF1"][i % 3], color: ["#4338CA", "#B45309", "#BE123C"][i % 3] }}
                title={p.full_name || p.name}
              >
                {initials(p.full_name || p.name)}
              </span>
            ))}
            {remainder > 0 && (
              <span className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[10px] font-bold border-2 border-white bg-slate-100 text-slate-600">
                +{remainder}
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function CountdownRing({ pct, label, tone }) {
  const SIZE = 52;
  const STROKE = 4;
  const R = (SIZE - STROKE) / 2;
  const C = 2 * Math.PI * R;
  const off = C * (1 - Math.max(0, Math.min(1, pct / 100)));
  return (
    <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden role="img">
      <circle cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none" stroke="#F1F2F8" strokeWidth={STROKE} />
      <circle
        cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none"
        stroke={tone} strokeWidth={STROKE} strokeLinecap="round"
        strokeDasharray={C} strokeDashoffset={off}
        transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
        style={{ transition: "stroke-dashoffset 0.4s ease" }}
      />
      <text x="50%" y="55%" textAnchor="middle" fontSize="12" fontWeight="700" fill="#0F172A">{label}</text>
    </svg>
  );
}

/* ─── Action strip ───────────────────────────────────────────────── */
function ActionStrip({ onOpen, onWhatsApp }) {
  const actions = [
    { id: "chase",   label: "Chase",       Icon: Send,         tone: "#6366F1", onClick: () => onOpen("email") },
    { id: "update",  label: "Update",      Icon: Pencil,       tone: "#0F172A", onClick: () => onOpen("note") },
    { id: "call",    label: "Call",        Icon: Phone,        tone: "#0F172A", onClick: () => onOpen("call") },
    { id: "wa",      label: "WhatsApp",    Icon: MessageCircle, tone: "#10B981", onClick: onWhatsApp },
    { id: "doc",     label: "Request Doc", Icon: FileText,     tone: "#0F172A", onClick: () => onOpen("upload") },
    { id: "note",    label: "Note",        Icon: MessageSquare, tone: "#0F172A", onClick: () => onOpen("note") },
  ];
  return (
    <div className="mt-4 px-4" data-testid="dealroom-mobile-actions">
      <div className="flex items-stretch gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory"
        style={{ scrollbarWidth: "none" }}
      >
        {actions.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={a.onClick}
            className="snap-start min-w-[86px] flex-1 h-[64px] rounded-xl bg-white inline-flex flex-col items-center justify-center gap-1 active:scale-[0.98] transition-transform"
            style={{
              border: "1px solid #EFF0F5",
              boxShadow: "0 2px 8px -6px rgba(15,23,42,0.08)",
            }}
            data-testid={`dealroom-mobile-action-${a.id}`}
          >
            <a.Icon size={16} style={{ color: a.tone }} strokeWidth={2} />
            <span className="text-[11px] font-semibold" style={{ color: a.id === "chase" ? "#4338CA" : "#0F172A" }}>
              {a.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── Action required card ───────────────────────────────────────── */
function ActionRequiredCard({ vm, deal, blocker, responsible, dealId, onResolved, onOpen, onWhatsApp }) {
  const [resolving, setResolving] = useState(false);

  const action   = blocker?.label || blocker?.title || "";
  const roleKey  = normaliseRole(blocker?.owner_role || responsible?.participant_type || responsible?.role);
  const roleLabel = ({
    seller_solicitor: "Seller Solicitor",
    buyer_solicitor:  "Buyer Solicitor",
    buyer:            "Buyer",
    seller:           "Seller",
    mortgage_broker:  "Mortgage Broker",
    agent:            "Agent",
  })[roleKey] || (roleKey ? roleKey.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) : "Responsible party not recorded");
  const name     = responsible?.full_name || responsible?.name || blocker?.responsible_name || (roleKey ? roleLabel : "Not assigned");

  const onMarkResolved = useCallback(async () => {
    if (resolving) return;
    setResolving(true);
    try {
      await api.post(`/api/v2/deals/${dealId}/activity`, {
        activity_type: "risk_cleared",
        title: `${action} resolved`,
        description: blocker?.responsible_name
          ? `Cleared by ${blocker.responsible_name}`
          : `Resolved by ${name}`,
        meta: {
          kind:             "manual_resolution",
          source:           "mobile_action_required",
          blocker_label:    action,
          responsible_id:   responsible?.id || responsible?.user_id,
          responsible_name: name,
          responsible_role: responsible?.participant_type || responsible?.role,
        },
      });
      invalidateDealActivity(dealId);
      bumpTodaySignal();
      onResolved?.();
      toast.success("Resolved · timeline updated", { duration: 1800 });
    } catch (e) {
      toast.error("Couldn't mark resolved. Try again.");
    } finally {
      setResolving(false);
    }
  }, [resolving, dealId, action, name, blocker, responsible, onResolved]);

  if (!vm) return null;
  const days = blocker?.age_days ?? blocker?.days_outstanding ?? null;
  const exStatus = exchangeTargetStatus(vm.dToExchange, isDealExchanged(deal));

  if (!blocker) {
    return (
      <section className="mt-4 mx-4 rounded-2xl bg-white px-4 py-3.5"
        style={{ border: "1px solid #EFF0F5" }}
        data-testid="dealroom-mobile-action-required"
      >
        <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: "#4338CA" }}>Action required</p>
        <p className="mt-2 text-[12.5px] text-slate-500" data-testid="action-required-empty">Nothing outstanding</p>
      </section>
    );
  }

  return (
    <section
      className="mt-4 mx-4 rounded-2xl bg-white relative overflow-hidden"
      style={{
        border: "1px solid #EFF0F5",
        boxShadow: "0 6px 18px -14px rgba(99,102,241,0.18)",
      }}
      data-testid="dealroom-mobile-action-required"
    >
      <span aria-hidden className="absolute left-0 top-0 bottom-0 w-[3px]"
        style={{ background: "linear-gradient(180deg, #6366F1, #7C3AED)" }}
      />
      <div className="px-4 py-3.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: "#4338CA" }}>
            Action required
            <span className="normal-case tracking-normal font-medium text-slate-400" data-testid="action-required-source">
              {blocker.source === "journey" ? " · next journey milestone" : " · open blocker"}
            </span>
          </p>
          <div className="flex items-center gap-1">
            <button type="button"
              className="w-7 h-7 inline-flex items-center justify-center rounded-full border border-slate-200 active:bg-slate-50"
              aria-label="Snooze"
              data-testid="action-required-snooze"
            >
              <Bell size={11} className="text-slate-500" />
            </button>
            <button type="button"
              className="w-7 h-7 inline-flex items-center justify-center rounded-full border border-slate-200 active:bg-slate-50"
              aria-label="Expand"
            >
              <ChevronDown size={11} className="text-slate-500" />
            </button>
          </div>
        </div>

        <div className="mt-2 flex items-start gap-3">
          <span
            className="w-11 h-11 rounded-full inline-flex items-center justify-center text-[12px] font-bold shrink-0"
            style={{ background: "#FEF3E8", color: "#B45309" }}
            aria-hidden
          >
            {initials(name)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[14.5px] font-bold text-slate-900 leading-tight truncate" data-testid="action-required-name">{name}</p>
            <p className="text-[11.5px] text-slate-500 mt-0.5" data-testid="action-required-role">{roleLabel}</p>
            <p className="mt-2 flex items-center gap-1.5 max-w-full text-[12px] font-semibold text-slate-700">
              <FileText size={11} className="shrink-0" style={{ color: "#B45309" }} />
              <span className="truncate min-w-0" data-testid="action-required-action">{action}</span>
            </p>
          </div>
        </div>

        <p className="mt-2.5 text-[11px] font-semibold inline-flex items-center gap-1.5" style={{ color: "#BE123C" }}>
          <Clock size={10} />
          {days !== null && <span>Outstanding for {days} day{days === 1 ? "" : "s"}</span>}
          {days !== null && exStatus && <span className="text-rose-300">·</span>}
          {exStatus && (
            <span data-testid="action-required-exchange-status">
              {exStatus.short === "Exchanged" ? "Exchanged" : `Exchange target ${exStatus.short === "Due today" ? "due today" : exStatus.short}`}
            </span>
          )}
        </p>

        <div className="mt-3 grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => onOpen("email")}
            className="h-[42px] rounded-xl inline-flex items-center justify-center gap-1.5 text-[12.5px] font-semibold text-white active:scale-[0.98] transition-transform"
            style={{
              background: "linear-gradient(180deg, #6366F1 0%, #5B5BF0 100%)",
              boxShadow: "0 6px 18px -10px rgba(99,102,241,0.55)",
            }}
            data-testid="action-required-chase"
          >
            <Send size={12} /> Chase
          </button>
          <button
            type="button"
            onClick={() => onOpen("call")}
            className="h-[42px] rounded-xl inline-flex items-center justify-center gap-1.5 text-[12.5px] font-semibold text-slate-700 border border-slate-200 bg-white active:bg-slate-50"
            data-testid="action-required-call"
          >
            <Phone size={12} /> Call
          </button>
          <button
            type="button"
            onClick={onWhatsApp}
            className="h-[42px] rounded-xl inline-flex items-center justify-center gap-1.5 text-[12.5px] font-semibold text-slate-700 border border-slate-200 bg-white active:bg-slate-50"
            data-testid="action-required-wa"
          >
            <MessageCircle size={12} style={{ color: "#10B981" }} /> WhatsApp
          </button>
        </div>

        {/* Calm tertiary action — closes the loop in one tap when
            the agent already has the answer in hand. */}
        <button
          type="button"
          onClick={onMarkResolved}
          disabled={resolving}
          className="mt-2.5 w-full h-9 inline-flex items-center justify-center gap-1.5 rounded-lg text-[11.5px] font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-50 disabled:opacity-60 active:bg-slate-100 transition-colors"
          data-testid="action-required-resolve"
        >
          <CheckCircle2 size={12} style={{ color: "#047857" }} />
          {resolving ? "Marking resolved…" : "Mark resolved"}
        </button>
      </div>
    </section>
  );
}

/* ─── Up Next ────────────────────────────────────────────────────── */
function SharedNextBestAction({ nba, onAction, onRequestDoc }) {
  const quiet = nba.terminal || nba.mosCompleted || nba.nbaMode === "mortgage_not_required";
  const title = nba.terminal ? "Transaction complete"
    : nba.mosCompleted ? "Memorandum of Sale issued"
    : nba.nbaMode === "mortgage_not_required" ? "Mortgage: Not Required"
    : nba.nbaTitle;
  const why = nba.terminal ? "No further actions on this deal."
    : nba.mosCompleted ? "Both solicitors have the Memorandum of Sale."
    : nba.nbaMode === "mortgage_not_required" ? "Cash buyer — the mortgage journey does not apply on this deal."
    : nba.nbaWhy;
  // Same CTA wording as the desktop card.
  const cta = nba.nbaMode === "blocker" ? "Resolve" : nba.confirmPending ? `Confirm · ${nba.nbaTitle}` : nba.nbaTitle;
  return (
    <section className="mt-4 mx-4 rounded-2xl bg-white overflow-hidden"
      style={{ border: "1px solid #EFF0F5", boxShadow: "0 2px 8px -6px rgba(15,23,42,0.05)" }}
      data-testid="dealroom-mobile-upnext"
    >
      <header className="px-4 pt-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-violet-700">Next Best Action</p>
      </header>
      <div className="px-4 pt-1.5 pb-3.5" data-testid="dealroom-mobile-upnext-item">
        <p className="text-[13px] font-semibold text-slate-900 leading-snug" data-testid="dealroom-mobile-upnext-title">{title}</p>
        {why && <p className="text-[11.5px] text-slate-500 mt-1 leading-relaxed" data-testid="dealroom-mobile-upnext-why">{why}</p>}
        {!quiet && (
          <button
            type="button"
            onClick={() => onAction?.(nba)}
            disabled={!!nba.permissionDenied}
            title={nba.permissionDenied ? "Your role can view this action but not complete it" : undefined}
            className={`mt-3 w-full min-h-[44px] rounded-xl text-[13px] font-semibold text-white disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 ${nba.nbaMode === "blocker" ? "bg-rose-600 hover:bg-rose-700" : nba.confirmPending ? "bg-emerald-600 hover:bg-emerald-700" : "bg-violet-600 hover:bg-violet-700"}`}
            data-testid="dealroom-mobile-upnext-cta"
          >
            {cta}
          </button>
        )}
        {!quiet && nba.showRequestDoc && onRequestDoc && (
          <button
            type="button"
            onClick={() => onRequestDoc()}
            className="mt-2 w-full min-h-[44px] rounded-xl text-[12.5px] font-semibold text-violet-700 bg-violet-50 hover:bg-violet-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
            data-testid="dealroom-mobile-upnext-request-doc"
          >
            Request
          </button>
        )}
      </div>
    </section>
  );
}

function UpNext({ deal, nba: shared = null, onNbaAction, onRequestDoc }) {
  // Same Next Best Action as the desktop card when the page supplies it.
  if (shared) return <SharedNextBestAction nba={shared} onAction={onNbaAction} onRequestDoc={onRequestDoc} />;
  // Fallback: canonical server next best action (routes_deals._derive_next_best_action).
  const nba = deal?.next_best_action || deal?.nextBestAction || null;
  const label = String(nba?.label || "").trim();
  const owner = nba?.owner_role
    ? String(nba.owner_role).replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())
    : (label ? "You" : "");

  return (
    <section className="mt-4 mx-4 rounded-2xl bg-white overflow-hidden"
      style={{ border: "1px solid #EFF0F5", boxShadow: "0 2px 8px -6px rgba(15,23,42,0.05)" }}
      data-testid="dealroom-mobile-upnext"
    >
      <header className="px-4 py-3 flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-violet-700">Up next</p>
        <ChevronRight size={12} className="text-slate-300" />
      </header>
      {label ? (
        <ul>
          <li className="px-4 py-2.5 flex items-center gap-3" data-testid="dealroom-mobile-upnext-item">
            <span aria-hidden className="w-1.5 h-1.5 rounded-full" style={{ background: "#6366F1" }} />
            <span className="w-7 h-7 rounded-lg inline-flex items-center justify-center" style={{ background: "rgba(99,102,241,0.08)" }}>
              <Flag size={12} className="text-violet-600" strokeWidth={2} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[12.5px] font-semibold text-slate-800 leading-snug line-clamp-2">{label}</p>
              <p className="text-[11px] text-slate-500 mt-0.5" data-testid="dealroom-mobile-upnext-owner">{owner}</p>
            </div>
          </li>
        </ul>
      ) : (
        <p className="px-4 pb-3 text-[12px] text-slate-500" data-testid="dealroom-mobile-upnext-empty">No upcoming actions</p>
      )}
    </section>
  );
}

/* ─── Unified live feed ──────────────────────────────────────────── */
function LiveFeed({ activity }) {
  const rows = (activity || []).slice(0, 20);
  if (rows.length === 0) {
    return (
      <section className="mt-4 mx-4 rounded-2xl bg-white px-4 py-10 text-center"
        style={{ border: "1px solid #EFF0F5" }}
        data-testid="dealroom-mobile-feed"
      >
        <Sparkles size={16} className="mx-auto text-slate-300" />
        <p className="text-[12.5px] font-semibold text-slate-700 mt-2">No activity yet</p>
        <p className="text-[11px] text-slate-500 mt-1">Updates will surface here as the transaction progresses.</p>
      </section>
    );
  }
  return (
    <section className="mt-4 mx-4" data-testid="dealroom-mobile-feed">
      <ol className="relative" role="list">
        {/* Continuous left rail */}
        <span aria-hidden className="absolute top-2 bottom-2 left-[15px] w-px bg-slate-200/70" />
        {rows.map((row, i) => {
          const d = decorateActivity(row);
          const tone = FEED_ICON[d.kind] || FEED_ICON.default;
          const Icon = tone.Icon;
          return (
            <li key={row.id || i} className="relative pl-10 py-2.5"
              data-testid={`dealroom-mobile-feed-row-${i}`}
            >
              <span aria-hidden className="absolute left-[10px] top-[15px] w-2.5 h-2.5 rounded-full"
                style={{ background: "#6366F1", border: "2px solid white", boxShadow: "0 0 0 1px #E2E8F0" }} />
              <div className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-lg inline-flex items-center justify-center shrink-0"
                  style={{ background: tone.bg, color: tone.fg }}
                  aria-hidden
                >
                  <Icon size={12} strokeWidth={2} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[12.5px] font-semibold text-slate-900 truncate">{d.title}</p>
                  {d.subtitle && (
                    <p className="text-[11.5px] text-slate-500 mt-0.5 line-clamp-2 leading-relaxed">{d.subtitle}</p>
                  )}
                </div>
                <p className="text-[10.5px] text-slate-400 tabular-nums shrink-0 mt-0.5">{formatRel(row.created_at)}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/* ─── Inline composer (smart quick) ──────────────────────────────── */
function QuickComposer({ dealId, onPosted, onOpen, followUp }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  // "followUp" is enabled when there's an active blocker AND a
  // responsible participant with an email. It carries the context
  // needed to send the chase alongside the note in a single tap.
  const canFollowUp = !!(followUp && followUp.enabled && followUp.responsible?.email);

  const post = useCallback(async () => {
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      await api.post(`/api/v2/deals/${dealId}/activity`, {
        activity_type: "message",
        title: body.length > 80 ? `${body.slice(0, 77)}…` : body,
        description: body,
        meta: { kind: "note", source: "mobile_composer" },
      });
      invalidateDealActivity(dealId);
      setText("");
      onPosted?.();
      toast.success("Posted to timeline", { duration: 1500 });
    } catch (e) {
      toast.error("Couldn't post just now. Try again.");
    } finally {
      setBusy(false);
    }
  }, [text, busy, dealId, onPosted]);

  // Combined gesture — post + chase. Calm, human, operational.
  // Never automated; always triggered by a deliberate tap.
  const postAndChase = useCallback(async () => {
    const body = text.trim();
    if (!body || busy || !canFollowUp) return;
    setBusy(true);
    try {
      // 1. The note (operational memory of what the agent saw / decided)
      await api.post(`/api/v2/deals/${dealId}/activity`, {
        activity_type: "message",
        title: body.length > 80 ? `${body.slice(0, 77)}…` : body,
        description: body,
        meta: { kind: "note", source: "mobile_composer_followup" },
      });
      // 2. The follow-up to the responsible party
      const responsible = followUp.responsible;
      const action = followUp.blocker?.label || followUp.blocker?.title || "outstanding item";
      const propertyLabel = followUp.propertyAddress || "this transaction";
      const greet = _firstName(responsible.full_name || responsible.name) || "there";
      const emailBody = (
        `Hi ${greet},\n\n` +
        `Quick follow-up on ${action.toLowerCase()} for ${propertyLabel}.\n\n` +
        `${body}\n\n` +
        `Could you confirm where we are on this?\n\n` +
        `Thanks`
      );
      const subject = `Follow-up: ${action}`;
      const res = await sendOperationalEmail({
        dealId,
        recipients: [{
          email:     responsible.email,
          full_name: responsible.full_name || responsible.name,
          role:      responsible.participant_type || responsible.role,
        }],
        subject,
        body: emailBody,
        kind: "chase",
      });
      invalidateDealActivity(dealId);
      setText("");
      onPosted?.();
      if (res.ok) {
        toast.success(`Posted · follow-up sent to ${greet}`, { duration: 2000 });
      } else {
        toast.success("Posted to timeline", { duration: 1500 });
        toast.message("Note saved — follow-up couldn't send. Try the Chase button.");
      }
    } catch (e) {
      toast.error("Couldn't post just now. Try again.");
    } finally {
      setBusy(false);
    }
  }, [text, busy, dealId, canFollowUp, followUp, onPosted]);

  // Combined gesture — post + schedule a quiet follow-up reminder
  // for a detected future day. Writes a task_created activity with
  // meta.kind=scheduled_chase so the operational timeline carries
  // forward what ChainFlow "remembered" for the agent.
  const postAndSchedule = useCallback(async (detected) => {
    const body = text.trim();
    if (!body || busy || !detected || !followUp?.responsible) return;
    setBusy(true);
    try {
      const responsible = followUp.responsible;
      // 1. The note
      const noteRes = await api.post(`/api/v2/deals/${dealId}/activity`, {
        activity_type: "message",
        title: body.length > 80 ? `${body.slice(0, 77)}…` : body,
        description: body,
        meta: { kind: "note", source: "mobile_composer_schedule" },
      });
      // 2. The scheduled follow-up — calm, operational, NOT
      //    automation. Surfaces in the timeline as "Follow-up
      //    scheduled for Friday".
      await api.post(`/api/v2/deals/${dealId}/activity`, {
        activity_type: "task_created",
        title: `Follow-up scheduled for ${detected.day}`,
        description: `With ${responsible.full_name || responsible.name}${followUp.blocker?.label ? ` · ${followUp.blocker.label}` : ""}`,
        meta: {
          kind:              "scheduled_chase",
          scheduled_for:     detected.iso,
          day_label:         detected.day,
          responsible_id:    responsible.id || responsible.user_id,
          responsible_name:  responsible.full_name || responsible.name,
          responsible_role:  responsible.participant_type || responsible.role,
          responsible_email: responsible.email,
          blocker_context:   followUp.blocker?.label || followUp.blocker?.title || null,
          source_note_id:    noteRes?.data?.id || null,
          source:            "mobile_composer_schedule",
        },
      });
      invalidateDealActivity(dealId);
      setText("");
      onPosted?.();
      toast.success(`Posted · follow-up set for ${detected.day}`, { duration: 2200 });
    } catch (e) {
      toast.error("Couldn't post just now. Try again.");
    } finally {
      setBusy(false);
    }
  }, [text, busy, dealId, followUp, onPosted]);

  const onKey = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); post(); }
  }, [post]);

  const quicks = [
    { id: "note",   label: "Note",   Icon: MessageSquare, tone: "#4338CA", action: () => onOpen("note") },
    { id: "call",   label: "Call",   Icon: Phone,         tone: "#B45309", action: () => onOpen("call") },
    { id: "email",  label: "Email",  Icon: Mail,          tone: "#0F172A", action: () => onOpen("email") },
    { id: "upload", label: "Upload", Icon: Upload,        tone: "#0F172A", action: () => onOpen("upload") },
    { id: "task",   label: "Task",   Icon: CheckCircle2,  tone: "#047857", action: () => onOpen("task") },
  ];

  const followUpName = canFollowUp
    ? _firstName(followUp.responsible.full_name || followUp.responsible.name) || "the responsible party"
    : "";
  const hasText = !!text.trim();
  // Quiet detection — only surfaces a schedule option when the
  // agent has clearly referenced a future weekday. The product
  // remembers; it never guesses.
  const detected = useMemo(() => hasText ? detectFollowUpDay(text) : null, [text, hasText]);
  const canSchedule = !!(detected && followUp?.responsible);

  return (
    <section className="mt-4 mx-4" data-testid="dealroom-mobile-composer">
      <div className="rounded-2xl bg-white p-2 flex items-center gap-2"
        style={{ border: "1px solid #EFF0F5", boxShadow: "0 4px 14px -10px rgba(15,23,42,0.10)" }}
      >
        <button type="button"
          className="w-9 h-9 inline-flex items-center justify-center rounded-full active:bg-slate-100 shrink-0"
          onClick={() => onOpen("upload")}
          aria-label="Attach"
          data-testid="dealroom-mobile-composer-attach"
        >
          <Paperclip size={14} className="text-slate-500" />
        </button>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          placeholder="Add an update…"
          className="flex-1 min-w-0 h-10 bg-transparent outline-none text-[13px] text-slate-900 placeholder-slate-400"
          data-testid="dealroom-mobile-composer-input"
        />
        {hasText ? (
          <button
            type="button"
            onClick={post}
            disabled={busy}
            className="w-9 h-9 inline-flex items-center justify-center rounded-full text-white shrink-0 disabled:opacity-60 active:scale-95 transition-transform"
            style={{ background: "linear-gradient(180deg, #6366F1, #5B5BF0)" }}
            aria-label="Send"
            data-testid="dealroom-mobile-composer-send"
          >
            <Send size={14} />
          </button>
        ) : (
          <button
            type="button"
            className="w-9 h-9 inline-flex items-center justify-center rounded-full text-white shrink-0"
            style={{ background: "linear-gradient(180deg, #6366F1, #5B5BF0)" }}
            aria-label="Voice note"
            data-testid="dealroom-mobile-composer-mic"
          >
            <Mic size={14} />
          </button>
        )}
      </div>

      {/* Combined "Post + Send follow-up" — only when there's text
          and a real responsible party to chase. Calm tertiary
          action, never automated. */}
      {canFollowUp && hasText && (
        <button
          type="button"
          onClick={postAndChase}
          disabled={busy}
          className="mt-2 w-full h-10 rounded-xl inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold disabled:opacity-60 active:scale-[0.99] transition-transform"
          style={{
            background: "rgba(99,102,241,0.06)",
            border: "1px solid rgba(99,102,241,0.18)",
            color: "#4338CA",
          }}
          data-testid="dealroom-mobile-composer-post-and-chase"
        >
          <Send size={11} />
          <span>Post + Send follow-up to {followUpName}</span>
        </button>
      )}

      {/* Combined "Post + Schedule follow-up for {Day}" — surfaces
          only when the agent's text references a specific future
          weekday. Calm "ChainFlow remembered this for me" feel. */}
      {canSchedule && hasText && (
        <button
          type="button"
          onClick={() => postAndSchedule(detected)}
          disabled={busy}
          className="mt-2 w-full h-10 rounded-xl inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold disabled:opacity-60 active:scale-[0.99] transition-transform"
          style={{
            background: "rgba(15,23,42,0.03)",
            border: "1px solid rgba(15,23,42,0.08)",
            color: "#0F172A",
          }}
          data-testid="dealroom-mobile-composer-post-and-schedule"
        >
          <Calendar size={11} style={{ color: "#4338CA" }} />
          <span>Post + Schedule follow-up for {detected.day}</span>
        </button>
      )}

      <div className="mt-2.5 flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1"
        style={{ scrollbarWidth: "none" }}
      >
        {quicks.map((q) => (
          <button
            key={q.id}
            type="button"
            onClick={q.action}
            className="h-9 px-3 rounded-full bg-white inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-700 active:bg-slate-50 shrink-0 transition-colors"
            style={{ border: "1px solid #EFF0F5" }}
            data-testid={`dealroom-mobile-composer-${q.id}`}
          >
            <q.Icon size={12} style={{ color: q.tone }} />
            <span>{q.label}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

/* ─── Collapsed sections (accordion) ─────────────────────────────── */
function CollapsedSection({ id, icon: Icon, label, count, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="mx-4 mt-2 rounded-2xl bg-white overflow-hidden"
      style={{ border: "1px solid #EFF0F5" }}
      data-testid={`dealroom-mobile-section-${id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 h-12 flex items-center gap-3 active:bg-slate-50"
        aria-expanded={open}
      >
        <Icon size={14} className="text-slate-500" strokeWidth={2} />
        <span className="flex-1 text-[12.5px] font-semibold text-slate-800 text-left">{label}</span>
        {count !== undefined && count !== null && (
          <span className="text-[11px] font-bold text-violet-700 tabular-nums">{count}</span>
        )}
        <ChevronDown size={11} className={`text-slate-400 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="px-4 pb-3" style={{ borderTop: "1px solid #F4F4F8" }}>{children}</div>
      )}
    </section>
  );
}

function ParticipantsList({ participants, dealId, onOpenEmail, onCall, onWhatsApp, canCreateContact = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const openContact = (contactId) => {
    saveDealReturnState(dealId, { section: "participants" });
    navigate(`/contacts/${contactId}`, {
      state: { fromDeal: { path: `${location.pathname}${location.search}${location.hash}` } },
    });
  };
  const rows = participants || [];
  if (rows.length === 0) return <p className="text-[11.5px] text-slate-500 py-3">No participants yet.</p>;
  return (
    <ul className="py-1">
      {rows.map((p, i) => {
        const role = ({
          buyer:            "Buyer",
          seller:           "Seller",
          buyer_solicitor:  "Buyer Solicitor",
          seller_solicitor: "Seller Solicitor",
          mortgage_broker:  "Mortgage Broker",
          agent:            "Agent",
          manager:          "Manager",
        })[(p.participant_type || p.role || "").toLowerCase()] || "Participant";
        const phone = p.phone || p.mobile;
        const contactId = p.contact_id || null;
        const roleKey = (p.participant_type || p.role || "participant").toLowerCase();
        const avatar = (
          <span className="w-8 h-8 rounded-full inline-flex items-center justify-center text-[10px] font-bold shrink-0"
            style={{ background: "#EEF0FE", color: "#4338CA" }}
          >
            {initials(p.full_name || p.name)}
          </span>
        );
        const nameBlock = (
          <>
            <p className={`text-[12.5px] font-semibold text-slate-900 truncate ${contactId ? "underline decoration-slate-300 decoration-dotted underline-offset-[3px]" : ""}`}>{p.full_name || p.name || "Participant"}</p>
            <p className="text-[10.5px] text-slate-500 truncate">
              {role}
              {!contactId && !p.is_internal && dealId && canCreateContact && (
                <>
                  {" · "}
                  <button
                    type="button"
                    className="font-semibold text-violet-700 active:underline"
                    onClick={(e) => { e.stopPropagation(); createContactForParticipant(dealId, p); }}
                    data-testid={`mobile-participant-create-contact-${roleKey}-${i}`}
                  >
                    Create contact
                  </button>
                </>
              )}
            </p>
          </>
        );
        return (
          <li key={p.id || p.user_id || i} className="py-2.5 flex items-center gap-3" style={{ borderTop: i === 0 ? "none" : "1px solid #F4F4F8" }} data-testid={`mobile-participant-${roleKey}-${i}`}>
            {contactId ? (
              <button
                type="button"
                onClick={() => openContact(contactId)}
                className="min-w-0 flex-1 flex items-center gap-3 text-left rounded-lg -my-1 py-1 touch-manipulation active:bg-slate-50"
                aria-label={`Open ${p.full_name || p.name || "participant"}'s contact profile`}
                data-testid={`mobile-participant-open-${roleKey}-${i}`}
              >
                {avatar}
                <div className="min-w-0 flex-1">{nameBlock}</div>
                <ChevronRight size={12} className="text-slate-300 shrink-0" aria-hidden />
              </button>
            ) : (
              <>
                {avatar}
                <div className="min-w-0 flex-1">{nameBlock}</div>
              </>
            )}
            <div className="flex items-center gap-1">
              {phone && (
                <a href={`tel:${phone}`} className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-slate-200 active:bg-slate-50" aria-label="Call">
                  <Phone size={12} className="text-slate-600" />
                </a>
              )}
              {phone && (
                <a href={`https://wa.me/${String(phone).replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-slate-200 active:bg-slate-50" aria-label="WhatsApp">
                  <MessageCircle size={12} style={{ color: "#10B981" }} />
                </a>
              )}
              {p.email && (
                <button type="button" onClick={() => onOpenEmail()} className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-slate-200 active:bg-slate-50" aria-label="Email">
                  <Mail size={12} className="text-slate-600" />
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function DocumentsList({ documents, onRequest }) {
  const rows = documents || [];
  if (rows.length === 0) {
    return (
      <div className="py-3 flex items-center justify-between">
        <p className="text-[11.5px] text-slate-500">No documents on file yet.</p>
        <button type="button" onClick={onRequest} className="text-[11.5px] font-semibold text-violet-700 inline-flex items-center gap-1">
          <FilePlus2 size={11} /> Request
        </button>
      </div>
    );
  }
  return (
    <ul className="py-1">
      {rows.slice(0, 10).map((d, i) => (
        <li key={d.id || i} className="py-2 flex items-center gap-3" style={{ borderTop: i === 0 ? "none" : "1px solid #F4F4F8" }}>
          <span className="w-7 h-7 rounded-md inline-flex items-center justify-center" style={{ background: "rgba(99,102,241,0.08)" }}>
            <FileText size={11} className="text-violet-600" />
          </span>
          <p className="flex-1 text-[12px] font-semibold text-slate-800 truncate">{d.name || d.file_name || "Document"}</p>
          <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-slate-400">{d.folder || ""}</span>
        </li>
      ))}
    </ul>
  );
}

/* ─── Root export ────────────────────────────────────────────────── */
export default function DealRoomMobile({
  deal, vm, prop, participants, documents, awaiting, activity,
  followed, onFollow,
  onOpenSheet,
  bumpActivity,
  journey,
  onAddParticipant,
  activityPanel = null, // canonical Radar (read-only) + Deal Activity, shared with desktop
  nba = null,           // desktop Next Best Action selector result (computeDealNextBestAction)
  onNbaAction,
  onRequestDoc,         // same Request Document sheet as the desktop card
  keyDates = null,      // canonical Key Dates summary, shared with desktop
  buyerPosition = null, // canonical Buyer Position card, shared with desktop
}) {
  const [returnSection] = useState(() => peekDealReturnState(deal?.id)?.section || null);

  // Journey record (same waiting_on as Deal Rooms list + Up Next) first; rule-based blockers only when it has nothing.
  const blocker = useMemo(() => {
    const wo = deal?.waiting_on || deal?.waitingOn || null;
    if (wo && (wo.milestone_label || wo.owner_role)) {
      return {
        label: wo.milestone_label || "",
        owner_role: wo.owner_role || null,
        source: "journey", // next outstanding journey milestone (same record as Deal Rooms list + Next Best Action)
        age_days: Number.isFinite(deal?.waiting_days) ? deal.waiting_days : null,
      };
    }
    const b = vm?.topBlocker || (awaiting && awaiting[0]) || null;
    if (!b) return null;
    // Accepts both the /blockers shape and the Deal Room's deal.blockers fallback shape (role_label, days).
    const age = Number.isFinite(b.age_days) ? b.age_days : (Number(b.days) > 0 ? Number(b.days) : null);
    return {
      ...b,
      source: "blocker",
      label: b.label || b.title || "",
      owner_role: b.owner_role || b.role_label || null,
      age_days: age,
      responsible_name: b.responsible_name || b.recipient?.full_name || null,
      recipient: b.recipient || null,
    };
  }, [deal, vm, awaiting]);
  const riskTone = riskFor(vm, deal);

  // Single source for the responsible participant — shared between
  // ACTION REQUIRED card and the composer follow-up combo so both
  // surfaces target the same person.
  const responsible = useMemo(() => {
    if (!blocker) return null;
    const desiredRole = normaliseRole(blocker.owner_role);
    const list = participants || [];
    // Open blocker: use the backend-resolved recipient (same resolver as the Radar's Waiting On).
    const r = blocker.source === "blocker" ? blocker.recipient : null;
    if (r) {
      return list.find((p) => (r.email && p.email === r.email) || (r.full_name && (p.full_name || p.name) === r.full_name)) || r;
    }
    return (
      (desiredRole && list.find((p) => normaliseRole(p.participant_type || p.role) === desiredRole)) ||
      blocker.recipient ||
      null
    );
  }, [participants, blocker]);

  const followUp = useMemo(() => ({
    // Combo enabled whenever there's a responsible party we can
    // chase. The ACTION REQUIRED card always renders for an active
    // transaction, and the agent's freshly-typed note is enough
    // context for a calm follow-up even if no formal blocker object
    // is loaded from /blockers.
    enabled: !!(responsible && (responsible.email)),
    blocker,
    responsible,
    propertyAddress: vm?.propertyAddress,
  }), [blocker, responsible, vm]);

  // WhatsApp deep-link aimed at the responsible participant (or first phone we find).
  const onWhatsApp = useCallback(() => {
    const target = responsible && (responsible.phone || responsible.mobile)
      ? responsible
      : (participants || []).find((p) => p.phone || p.mobile);
    const phone = target?.phone || target?.mobile;
    if (!phone) {
      toast.message("No mobile number on file for the responsible party.");
      return;
    }
    const item = blocker?.label ? blocker.label.toLowerCase() : "the outstanding items";
    const text = encodeURIComponent(`Quick chase on ${vm?.propertyAddress || "the transaction"} — any update on ${item}?`);
    window.open(`https://wa.me/${String(phone).replace(/[^0-9]/g, "")}?text=${text}`, "_blank", "noopener");
  }, [responsible, participants, vm, blocker]);

  if (!deal) return null;

  return (
    <div className="min-h-screen cf-dealroom-mobile-pad" style={{ background: "#FAFAFC" }} data-testid="dealroom-mobile">
      <TopBar followed={followed} onFollow={onFollow} />
      <Hero deal={deal} vm={vm} prop={prop} participants={participants} activity={activity} riskTone={riskTone} />
      <ActionStrip onOpen={onOpenSheet} onWhatsApp={onWhatsApp} />
      {buyerPosition && <div className="mt-4 mx-4 min-w-0" data-testid="dealroom-mobile-buyer-position">{buyerPosition}</div>}
      <ActionRequiredCard
        vm={vm}
        deal={deal}
        blocker={blocker}
        responsible={responsible}
        dealId={deal.id}
        onResolved={bumpActivity}
        onOpen={onOpenSheet}
        onWhatsApp={onWhatsApp}
      />
      <UpNext deal={deal} nba={nba} onNbaAction={onNbaAction} onRequestDoc={onRequestDoc} />
      {activityPanel ? (
        <div className="mt-4 mx-4 min-w-0" data-testid="dealroom-mobile-activity-panel">{activityPanel}</div>
      ) : <LiveFeed activity={activity} />}
      <QuickComposer
        dealId={deal.id}
        onPosted={bumpActivity}
        onOpen={onOpenSheet}
        followUp={followUp}
      />
      <div className="mt-4 space-y-0">
        <CollapsedSection id="participants" icon={Users} label="Participants" count={(participants || []).length} defaultOpen={returnSection === "participants"}>
          <ParticipantsList
            participants={participants}
            dealId={deal.id}
            canCreateContact={!!onAddParticipant}
            onOpenEmail={() => onOpenSheet("email")}
          />
          {onAddParticipant && (
            <div className="flex items-center gap-4 pt-1 pb-1">
              {["buyer", "seller"].map((r) => (
                <button key={r} type="button" onClick={() => onAddParticipant(r)}
                  className="inline-flex items-center gap-1 min-h-[36px] text-[11.5px] font-semibold text-violet-700 touch-manipulation"
                  data-testid={`mobile-participants-add-another-${r}`}
                >
                  + Add another {r}
                </button>
              ))}
            </div>
          )}
        </CollapsedSection>
        <CollapsedSection id="documents" icon={Folder} label="Documents" count={(documents || []).length}>
          <DocumentsList documents={documents} onRequest={() => onOpenSheet("upload")} />
        </CollapsedSection>
        <CollapsedSection id="timeline" icon={Flag} label="Transaction journey">
          <div className="px-3 pt-1 pb-3" data-testid="dealroom-mobile-journey">
            {(String(deal?.stage || "").toLowerCase() === "completed" || deal?.completed_at || deal?.completedAt) && (
              <p className="mb-2 text-[13px] text-slate-600" data-testid="dealroom-mobile-journey-completed">Transaction completed</p>
            )}
            {journey}
          </div>
        </CollapsedSection>
        <CollapsedSection id="key-dates" icon={Calendar} label="Key dates">
          {keyDates}
        </CollapsedSection>
      </div>
    </div>
  );
}
